import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  User as PersonIcon,
  Mail as EmailIcon,
  Phone as PhoneIcon,
  MapPin as LocationIcon,
  X as CloseIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  FileText,
  Home,
  Tag,
} from 'lucide-react';
import { Customer } from '@/utils/localStorage';

interface NewCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode' | 'messageSettings' | 'deliveryHistory' | 'mergeHistory' | 'mergeCandidates' | 'customFields'>) => void;
}

const NewCustomerModal: React.FC<NewCustomerModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    nameKana: '',
    email: '',
    phone: '',
    birthday: '',
    occupation: '',
    address: '',
    allergies: '',
    skinTypes: [] as string[],
    hairTypes: [] as string[],
    preferences: '',
    preferredStaff: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSelectChange = (field: string) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // 必須項目のチェック
    if (!formData.name.trim()) {
      newErrors.name = 'お名前は必須です';
    }

    if (!formData.nameKana.trim()) {
      newErrors.nameKana = 'お名前（カナ）は必須です';
    } else if (!/^[ァ-ヶー\s]+$/.test(formData.nameKana)) {
      newErrors.nameKana = 'カタカナで入力してください';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号は必須です';
    } else if (!/^[\d-]+$/.test(formData.phone)) {
      newErrors.phone = '電話番号の形式が正しくありません';
    }

    // メールアドレスの形式チェック（任意項目）
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'メールアドレスの形式が正しくありません';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags(prev => [...prev, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode' | 'messageSettings' | 'deliveryHistory' | 'mergeHistory' | 'mergeCandidates' | 'customFields'> = {
      ...formData,
      lastVisit: new Date().toISOString().split('T')[0],
      totalVisits: 0,
      totalSpent: 0,
      status: 'active',
      rank: 'bronze',
      tags,
      visitHistory: [],
    };

    onSave(customerData);
    handleReset();
  };

  const handleReset = () => {
    setFormData({
      name: '',
      nameKana: '',
      email: '',
      phone: '',
      birthday: '',
      occupation: '',
      address: '',
      allergies: '',
      skinTypes: [],
      hairTypes: [],
      preferences: '',
      preferredStaff: '',
    });
    setTags([]);
    setNewTag('');
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PersonIcon className="h-5 w-5 text-primary" />
            新規顧客登録
          </DialogTitle>
          <DialogDescription>
            新しい顧客情報を登録します。必須項目を入力して登録ボタンを押してください。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pb-6">
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              <span className="font-semibold">必須項目:</span> お名前、お名前（カナ）、電話番号<br />
              その他の項目は後から編集できます。
            </AlertDescription>
          </Alert>

          {/* 基本情報 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              基本情報
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  お名前 <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <PersonIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    className={`pl-9 ${errors.name ? 'border-destructive' : ''}`}
                  />
                </div>
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nameKana">
                  お名前（カナ） <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="nameKana"
                  value={formData.nameKana}
                  onChange={handleInputChange('nameKana')}
                  placeholder="ヤマダ ハナコ"
                  className={errors.nameKana ? 'border-destructive' : ''}
                />
                {errors.nameKana && <p className="text-sm text-destructive">{errors.nameKana}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  電話番号 <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    placeholder="090-1234-5678"
                    className={`pl-9 ${errors.phone ? 'border-destructive' : ''}`}
                  />
                </div>
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <div className="relative">
                  <EmailIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    className={`pl-9 ${errors.email ? 'border-destructive' : ''}`}
                  />
                </div>
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthday">生年月日</Label>
                <Input
                  id="birthday"
                  type="date"
                  value={formData.birthday}
                  onChange={handleInputChange('birthday')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="occupation">ご職業</Label>
                <Input
                  id="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange('occupation')}
                  placeholder="会社員、主婦など"
                />
              </div>
            </div>
          </div>

          {/* 詳細情報 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              詳細情報
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address">ご住所</Label>
                <div className="relative">
                  <LocationIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={handleInputChange('address')}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {/* 肌質選択（複数選択） */}
                <div className="space-y-2">
                  <Label>肌質（複数選択可）</Label>
                  <div className="flex flex-wrap gap-2">
                    {["普通肌", "敏感肌", "乾燥肌", "脂性肌", "混合肌"].map((type) => (
                      <Badge
                        key={type}
                        variant={formData.skinTypes.includes(type) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            skinTypes: prev.skinTypes.includes(type)
                              ? prev.skinTypes.filter(t => t !== type)
                              : [...prev.skinTypes, type]
                          }));
                        }}
                      >
                        {type}
                      </Badge>
                    ))}
                  </div>
                  {formData.skinTypes.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      選択中: {formData.skinTypes.join(", ")}
                    </p>
                  )}
                </div>

                {/* 髪質選択（複数選択） */}
                <div className="space-y-2">
                  <Label>髪質（複数選択可）</Label>
                  <div className="flex flex-wrap gap-2">
                    {["普通髪", "細毛", "太毛", "くせ毛", "直毛", "ダメージ毛"].map((type) => (
                      <Badge
                        key={type}
                        variant={formData.hairTypes.includes(type) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            hairTypes: prev.hairTypes.includes(type)
                              ? prev.hairTypes.filter(t => t !== type)
                              : [...prev.hairTypes, type]
                          }));
                        }}
                      >
                        {type}
                      </Badge>
                    ))}
                  </div>
                  {formData.hairTypes.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      選択中: {formData.hairTypes.join(", ")}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferredStaff">担当希望スタッフ</Label>
                <Select value={formData.preferredStaff} onValueChange={handleSelectChange('preferredStaff')}>
                  <SelectTrigger id="preferredStaff">
                    <SelectValue placeholder="どなたでも" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="佐藤">佐藤</SelectItem>
                    <SelectItem value="田中">田中</SelectItem>
                    <SelectItem value="山田">山田</SelectItem>
                    <SelectItem value="鈴木">鈴木</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="allergies">アレルギー・注意事項</Label>
                <Textarea
                  id="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange('allergies')}
                  placeholder="特定の成分にアレルギーがある場合など"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferences">ご希望・ご要望</Label>
                <Textarea
                  id="preferences"
                  value={formData.preferences}
                  onChange={handleInputChange('preferences')}
                  placeholder="リラックス重視、強めの圧など"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* タグ管理 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              タグ
            </h3>

            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="VIP、常連など"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
                className="min-w-[80px]"
              >
                追加
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground" onClick={() => handleRemoveTag(tag)}>
                  {tag}
                  <CloseIcon className="ml-1 h-3 w-3" />
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* フッターボタン */}
        <div className="sticky bottom-0 left-0 right-0 flex justify-between gap-3 pt-4 border-t bg-background">
          <Button
            onClick={handleClose}
            variant="outline"
            className="min-h-[48px] min-w-[120px]"
          >
            <CloseIcon className="mr-2 h-4 w-4" />
            キャンセル
          </Button>

          <Button
            onClick={handleSubmit}
            variant="default"
            className="min-h-[48px] min-w-[120px]"
          >
            <SaveIcon className="mr-2 h-4 w-4" />
            登録
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewCustomerModal;
